import React from "react";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useSelector } from "react-redux";

const ITEM_HEIGHT = 42;
const ITEM_PADDING_TOP = 6;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 260,
      scrollbarWidth: "thin",
      scrollbarColor: "#c1c1c1 #f5f5f5",
    },
  },
};

const extractName = (email) => {
  if (!email) return "";
  const namePart = email.split("@")[0];
  return namePart
    .split(/[._]/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
};

const getSortedEmails = (emails) =>
  Array.isArray(emails)
    ? [...emails].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" }),
      )
    : [];

export default function UsersDropdown({
  selectedEmails = [],
  onSelectionChange,
}) {
  const { usersList: rawEmails, usersListFetching: emailLoading } = useSelector(
    (state) => state.dashboard,
  );

  const emailToShow = emailLoading ? [] : getSortedEmails(rawEmails);

  const handleChange = (event) => {
    const { value } = event.target;
    onSelectionChange(typeof value === "string" ? value.split(",") : value);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onSelectionChange([]);
  };

  return (
    <FormControl sx={{ m: 1, width: 290, position: "relative" }}>
      <InputLabel
        id="user-email-select-label"
        sx={{
          fontSize: "0.85rem",
          transform: "translate(14px, 10px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
          },
        }}
      >
        Select Users
      </InputLabel>

      <Select
        labelId="user-email-select-label"
        multiple
        value={selectedEmails}
        onChange={handleChange}
        input={
          <OutlinedInput
            label="Select Users"
            sx={{
              height: 40,
              display: "flex",
              alignItems: "center",
              pr: selectedEmails.length > 0 ? 6 : 2, // ✅ fixed bug
            }}
          />
        }
        renderValue={(selected) => {
          if (!selected.length) return null;
          const [first, ...rest] = selected;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                overflow: "hidden",
              }}
            >
              <Tooltip title={first} arrow>
                <Chip
                  label={extractName(first)}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.75rem",
                    "& .MuiChip-label": { px: 0.6 },
                  }}
                />
              </Tooltip>
              {rest.length > 0 && (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  +{rest.length} more
                </Typography>
              )}
            </Box>
          );
        }}
        MenuProps={MenuProps}
        sx={{
          height: 36,
          width: 230,
          "& .MuiSelect-select": { paddingY: 0.5, fontSize: "14px" },
          "&.MuiSelect-root": { borderRadius: "10px" },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1976d2",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1976d2",
          },
        }}
      >
        {emailLoading && <MenuItem disabled>Loading...</MenuItem>}
        {!emailLoading && emailToShow.length === 0 && (
          <MenuItem disabled>No users found</MenuItem>
        )}
        {!emailLoading &&
          emailToShow.map((email) => (
            <MenuItem
              key={email}
              value={email}
              sx={{
                fontSize: "13px",
                marginBottom: "3px",
                borderRadius: "3px",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <Tooltip title={email} arrow placement="right">
                <span
                  style={{
                    display: "inline-block",
                    width: "100%",
                    fontSize: "13px",
                    color: "#000",
                  }}
                >
                  {extractName(email)}
                </span>
              </Tooltip>
            </MenuItem>
          ))}
      </Select>

      {selectedEmails.length > 0 && (
        <IconButton
          size="small"
          onClick={clearAll}
          sx={{
            position: "absolute",
            right: 28,
            top: "50%",
            transform: "translateY(-50%)",
            color: "text.secondary",
            zIndex: 2,
            p: 0.4,
            "&:hover": { color: "text.primary" },
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </FormControl>
  );
}
